import React, { Component } from 'react'
import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { inject, observer } from 'mobx-react'
import { CreateProduct, CreateChildProduct } from '../ProductStore'
import { AsyncSKUSearchInput } from '../../../../../components/Input/AsyncSearch/AsyncSKUSearchInput'
import { CurrencyInput } from '../../../../../components/CurrencyInput'
import { ThumbnailImage } from '../Components/ThumbnailImage'
import { API, IBaseProduct, INestedProduct } from '@getgreenline/homi-shared'
import { TooltipWithIcon } from '../../../../../components/TooltipWithIcon'
import { ConvertVariantModal } from './ConvertVariantModal'
import { Routes } from '../../../../../constants/Routes'
import { PricingTab } from '../DetailsSection/Pricing/PricingTab'
import { DiscountsTab } from '../DetailsSection/DiscountsTab'
import { DescriptionTab } from './Descriptions'
import { DeleteWarningModal } from '../DeleteWarningModal'
import { MergeProductModal } from './MergeProductModal'
import { CustomFieldsTab } from '../DetailsSection/CustomFieldsTab'
import { Details } from '../DetailsSection/Details'
import { Cannabinoids } from '../DetailsSection/Cannabinoids'
import { message, Icon, Collapse } from 'antd'
import { AsyncNameSearchInput } from '../../../../../components/Input/AsyncSearch/AsyncNameSearchInput'
import { BarcodeTab } from '../Components/BarcodeTab'
import { GrRegex, parseErrorMsg } from '../../../../../utilities/helpers'
import { ProductTab, ProductTabs } from '../DetailsSection/ProductTabs'
import { Row, Col, Tooltip } from 'antd-v4'
import { InventoryTab } from '../DetailsSection/InventoryTab'
import { CannabinoidsBatch } from '../DetailsSection/CannabinoidsBatch'
import { VariantWarningModal } from './VariantWarningModal'
import './EditVariant.scss'
import { ProductEventLogs } from '../ProductEventLogs'
import { Tags } from '../DetailsSection/Tags'

interface Props {
  index: number
  parentProduct: CreateProduct
  childProduct: CreateChildProduct
  currentCompanyStore?: CurrentCompanyStore
  onDelete: () => void
  searchDuplicateSkus: (inputSku: string, productResults: IBaseProduct[]) => void
  onRemoveChildProduct: () => void
  blockSaving: (blockSaving: boolean, errorMessage?: string) => void
  loadProduct: (productId: string) => void
  tagsToBeCreated: { [tagName: string]: number | undefined }
  updateCreatedTagsContext?: (tagName: string, value: number, callback?: () => void) => void
}

interface State {
  showConvertVariantToStandaloneModal: 'standalone' | 'variant' | undefined
  showMergeModal: boolean
  showThcCdbVariant: boolean
  showDeleteModal: boolean
  selectedChildProduct?: INestedProduct
  openVariantConfirmModal: boolean
  showLogsDrawer: boolean
}

@inject('currentCompanyStore')
@observer
export class EditVariant extends Component<Props, State> {
  company = this.props.currentCompanyStore!.company!

  constructor(props: Props) {
    super(props)

    this.state = {
      showConvertVariantToStandaloneModal: undefined,
      showMergeModal: false,
      showThcCdbVariant: false,
      showDeleteModal: false,
      selectedChildProduct: undefined,
      openVariantConfirmModal: false,
      showLogsDrawer: false,
    }
  }

  componentDidMount = async () => {
    const { childProduct } = this.props

    if (!childProduct.id) return

    await this.getNestedChildProduct(childProduct.id)
  }

  getNestedChildProduct = async (childProductId: string) => {
    try {
      const selectedChildProduct = await API.getProductById(this.company.id, childProductId)
      this.setState({ selectedChildProduct })
    } catch (error) {
      message.error(error)
    }
  }

  closeVariantModal = () => {
    this.setState({ openVariantConfirmModal: false })
  }

  onToggleActive = () => {
    this.props.childProduct.setIsActive(!this.props.childProduct.isActive)
  }

  undeleteChildProduct = async (childProduct: INestedProduct) => {
    const updateObject = this.props.parentProduct.undelete(childProduct)

    try {
      const undeletedProduct = await this.props.currentCompanyStore!.productStore!.editProduct(
        this.company.id,
        childProduct.parentProductId!,
        updateObject,
      )
      message.success(`${undeletedProduct.name} successfully undeleted`)
      this.props.loadProduct(childProduct.parentProductId!)
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error undeleting your product')
    }
  }

  getTags = () => {
    return [
      ...Object.entries(this.props.tagsToBeCreated)
        .filter(
          ([tagName, count]) =>
            count && !this.props.currentCompanyStore?.tags?.find((tag) => tag.name === tagName),
        )
        .map(([tagName]) => ({
          id: '',
          name: tagName,
          companyId: this.company.id,
          createDate: new Date(),
          updateDate: new Date(),
          deleteDate: null,
        })),
      ...(this.props.currentCompanyStore?.tags ?? []),
    ]
  }

  render() {
    const { parentProduct, childProduct, searchDuplicateSkus, index, currentCompanyStore } =
      this.props
    const { showConvertVariantToStandaloneModal } = this.state
    const isParentOrBulkProduct =
      parentProduct.pricingGroupId ||
      !(childProduct.trackInventory && childProduct.parentProduct.id)

    const deleteDisabled = !!parentProduct.pricingGroupId && !!childProduct.id

    const isBatchTracked = childProduct.parentProduct.isBatchTracked
    const canUseBatchCannabinoid = this.props.currentCompanyStore?.canUseCannabinoidPerBatch

    return (
      <>
        <Collapse.Panel
          id={this.props.childProduct.isActive ? 'active-variant' : 'inactive-variant'}
          {...this.props}
          key={index}
          header={
            <Row className='flex-row' justify='space-between' onClick={(e) => e.stopPropagation()}>
              <Tooltip
                title={'Variant is ' + (this.props.childProduct.isActive ? 'active' : 'inactive')}
              >
                <div
                  style={{
                    width: '5px',
                    marginRight: '40px',
                    marginLeft: '-45px',
                  }}
                ></div>
              </Tooltip>
              <Col className='d-flex align-items-center'>
                <ThumbnailImage
                  product={childProduct}
                  width={30}
                  iconStyle={{
                    height: '30px',
                    lineHeight: '30px',
                    verticalAlign: 'middle',
                  }}
                />
              </Col>

              <Col flex={1} className='d-flex align-items-center'>
                <Row className='flex-row flex-wrap' style={{ flex: 1, marginBottom: -8 }}>
                  <Col flex={1} className='ml-2 mb-2'>
                    <label>SKU</label>
                    <AsyncSKUSearchInput
                      value={childProduct.sku || ''}
                      size='default'
                      disabled={parentProduct.isDeleted}
                      searchDuplicateSkus={searchDuplicateSkus}
                      selectedChildProduct={this.state.selectedChildProduct}
                      onChange={(sku) => childProduct.setSKU(sku)}
                      placeholder='SKU'
                    />
                  </Col>

                  <Col flex={1} className='ml-2 mb-2'>
                    <label>
                      Name <span className='blaze-text-danger font-italic'>* required</span>
                    </label>
                    <AsyncNameSearchInput
                      productId={childProduct.id}
                      value={childProduct.name}
                      size='default'
                      placeholder='Name (required)'
                      disabled={!!childProduct.pricingGroupId || parentProduct.isDeleted}
                      onChange={(name) => childProduct.setName(GrRegex.replaceLatin1Space(name))}
                      blockSaving={this.props.blockSaving}
                    />
                  </Col>

                  <Col className='ml-2 mb-2'>
                    <label>Retail Price</label>
                    <Row className='d-flex flex-row'>
                      <Col className='mr-2'>
                        <CurrencyInput
                          className='gr-currency-input'
                          onlyPositiveNumber
                          centValue={childProduct.price}
                          disabled={!!childProduct.pricingGroupId || parentProduct.isDeleted}
                          onChange={(newValue) => childProduct.setPrice(newValue)}
                        />
                      </Col>

                      <Col>
                        <UncontrolledDropdown size='sm'>
                          <DropdownToggle color='light'>
                            <Icon type='ellipsis' rotate={90} />
                          </DropdownToggle>
                          <DropdownMenu className='background-shadow'>
                            {parentProduct.isDeleted ? (
                              <DropdownItem
                                className='blaze-text-danger'
                                onClick={() =>
                                  this.undeleteChildProduct(this.state.selectedChildProduct!)
                                }
                              >
                                Undelete
                              </DropdownItem>
                            ) : (
                              <>
                                <DropdownItem
                                  disabled={
                                    !childProduct.id ||
                                    !!isParentOrBulkProduct ||
                                    parentProduct.isDeleted
                                  }
                                  onClick={() =>
                                    this.setState({
                                      showConvertVariantToStandaloneModal: 'standalone',
                                    })
                                  }
                                >
                                  Split to standalone
                                  {isParentOrBulkProduct && (
                                    <TooltipWithIcon
                                      tooltipIconClassName='fa fa-info-circle blaze-text-muted ml-2'
                                      tooltipId='split-variant'
                                      tooltipContent={
                                        <div>
                                          {parentProduct.pricingGroupId
                                            ? 'You cannot split a product with a pricing group. If you want to split this product, you will need to remove the pricing group first.'
                                            : 'You cannot split a bulk product. If you want to split this product, you should create a new standalone product.'}
                                        </div>
                                      }
                                    />
                                  )}
                                </DropdownItem>
                                <DropdownItem
                                  disabled={
                                    !childProduct.id ||
                                    !!isParentOrBulkProduct ||
                                    parentProduct.isDeleted
                                  }
                                  onClick={() =>
                                    this.setState({
                                      showConvertVariantToStandaloneModal: 'variant',
                                    })
                                  }
                                >
                                  Split to variant
                                  {isParentOrBulkProduct && (
                                    <TooltipWithIcon
                                      tooltipIconClassName='fa fa-info-circle blaze-text-muted ml-2'
                                      tooltipId='split-variant'
                                      tooltipContent={
                                        <div>
                                          {parentProduct.pricingGroupId
                                            ? 'You cannot split a product with a pricing group. If you want to split this product, you will need to remove the pricing group first.'
                                            : 'You cannot split a bulk product. If you want to split this product, you should create a new standalone product.'}
                                        </div>
                                      }
                                    />
                                  )}
                                </DropdownItem>
                                <DropdownItem
                                  disabled={
                                    !childProduct.id ||
                                    !!isParentOrBulkProduct ||
                                    parentProduct.isDeleted
                                  }
                                  onClick={() => this.setState({ showMergeModal: true })}
                                >
                                  Merge
                                </DropdownItem>
                                <DropdownItem
                                  onClick={async () => {
                                    // Only show the warning if the product has already been created in the API
                                    if (!childProduct.id) {
                                      this.props.onDelete()
                                      return
                                    }

                                    if (this.state.selectedChildProduct === undefined) {
                                      await this.getNestedChildProduct(childProduct.id)
                                    }

                                    this.setState({ showDeleteModal: true })
                                  }}
                                  disabled={deleteDisabled || parentProduct.isDeleted}
                                >
                                  <span
                                    className={
                                      deleteDisabled ? 'blaze-text-muted' : 'blaze-text-danger'
                                    }
                                  >
                                    Delete
                                    {deleteDisabled && (
                                      <TooltipWithIcon
                                        tooltipIconClassName='fa fa-info-circle blaze-text-muted ml-2'
                                        tooltipId='delete-variant'
                                        tooltipContent={
                                          <span>
                                            You cannot delete a variant with a pricing group. If you
                                            want to delete this variant, you will need to remove the
                                            pricing group first
                                          </span>
                                        }
                                      />
                                    )}
                                  </span>
                                </DropdownItem>
                                <DropdownItem
                                  onClick={() => this.setState({ openVariantConfirmModal: true })}
                                >
                                  {this.props.childProduct.isActive
                                    ? 'Make inactive'
                                    : 'Make active'}
                                  <TooltipWithIcon
                                    tooltipIconClassName='fa fa-info-circle blaze-text-muted ml-2'
                                    tooltipId='inactive-variant'
                                    tooltipContent={<div>If inactive, will not show up on POS</div>}
                                  />
                                </DropdownItem>
                              </>
                            )}
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
            </Row>
          }
        >
          <ProductTabs
            tabs={[
              {
                tab: ProductTab.PRICING,
                content: (
                  <PricingTab
                    product={this.props.childProduct}
                    saveProductFirstWarning={undefined}
                  />
                ),
              },
              {
                tab: ProductTab.DISCOUNTS,
                content: <DiscountsTab product={this.props.childProduct} />,
              },
              {
                tab: ProductTab.BARCODE,
                content: <BarcodeTab product={this.props.childProduct} />,
              },
              {
                tab: ProductTab.DESCRIPTIONS,
                content: <DescriptionTab childProduct={this.props.childProduct} />,
              },
              {
                tab: ProductTab.DETAILS,
                content: <Details product={this.props.childProduct} />,
              },
              {
                tab: ProductTab.CANNABINOIDS,
                content: <Cannabinoids product={childProduct} />,
                isVisible:
                  canUseBatchCannabinoid === false ||
                  (canUseBatchCannabinoid === true && !isBatchTracked),
              },
              {
                tab: ProductTab.CANNABINOIDS_BATCH,
                content: <CannabinoidsBatch product={childProduct} />,
                isVisible: canUseBatchCannabinoid === true && isBatchTracked,
              },
              {
                tab: ProductTab.TAGS,
                content: (
                  <Tags
                    tags={this.getTags() ?? []}
                    companyId={this.company.id}
                    product={this.props.childProduct}
                    updateCreatedTagsContext={this.props.updateCreatedTagsContext}
                  />
                ),
              },
              {
                tab: ProductTab.CUSTOM_FIELDS,
                content: (
                  <CustomFieldsTab
                    blockSaving={this.props.blockSaving}
                    product={this.props.childProduct}
                  />
                ),
              },
              {
                tab: ProductTab.INVENTORY,
                isVisible: this.props.childProduct.trackInventory,
                content: <InventoryTab product={this.props.childProduct} />,
              },
              {
                tab: ProductTab.EDIT_HISTORY,
                isVisible: !!this.props.childProduct.id,
                content: <ProductEventLogs product={this.props.childProduct} />,
              },
            ]}
          />
        </Collapse.Panel>

        {showConvertVariantToStandaloneModal && childProduct.id && (
          <ConvertVariantModal
            mode={showConvertVariantToStandaloneModal}
            parentName={parentProduct.name}
            productId={childProduct.id}
            productName={childProduct.name}
            onHide={() => {
              this.setState({ showConvertVariantToStandaloneModal: undefined })
            }}
            onSubmit={(newProduct) => {
              window.open(Routes.EDIT_PRODUCT(this.company.id, newProduct.id), '_blank')
              this.props.onRemoveChildProduct()
              this.setState({ showConvertVariantToStandaloneModal: undefined })
            }}
          />
        )}

        {this.state.showMergeModal && childProduct.id && (
          <MergeProductModal
            onClose={() => this.setState({ showMergeModal: false })}
            onSubmit={(newProduct) => {
              window.open(Routes.EDIT_PRODUCT(this.company.id, newProduct.id), '_blank')
              this.setState({ showMergeModal: false })
              this.props.onRemoveChildProduct()
            }}
            productId={childProduct.id}
            isParentBatchTracked={parentProduct.isBatchTracked}
          />
        )}

        {childProduct && this.state.showDeleteModal && (
          <DeleteWarningModal
            product={this.state.selectedChildProduct}
            productName={`${parentProduct.name} - ${childProduct.name}`}
            isVariant={true}
            hasVariants={false}
            isLoading={false}
            submit={() => {
              this.props.onDelete()
              this.setState({ showDeleteModal: false })
            }}
            onClose={() => this.setState({ showDeleteModal: false })}
            isVisible={this.state.showDeleteModal}
          />
        )}
        <VariantWarningModal
          visible={this.state.openVariantConfirmModal}
          onClose={this.closeVariantModal}
          onToggleActive={this.onToggleActive}
          isActive={this.props.childProduct.isActive}
        />
      </>
    )
  }
}
